import React, { useState } from 'react';
import {
 SunIcon,
 MoonIcon,
 ComputerDesktopIcon,
 SwatchIcon
} from '@heroicons/react/24/outline';
import {
 SunIcon as SunIconSolid,
 MoonIcon as MoonIconSolid,
 ComputerDesktopIcon as ComputerDesktopIconSolid
} from '@heroicons/react/24/solid';
import { useTheme } from '../contexts/ThemeContext';

const ThemeToggle = ({ className = '' }) => {
 const { theme, changeTheme } = useTheme();
 const [isOpen, setIsOpen] = useState(false);

 const handleThemeChange = (newTheme) => {
 changeTheme(newTheme);
 setIsOpen(false);
 };

 const themes = [
 {
 id: 'light',
 name: 'Light',
 icon: <SunIconSolid className="h-4 w-4" />,
 description: 'Light mode for bright environments',
 preview: 'bg-white border-gray-200'
 },
 {
 id: 'dark',
 name: 'Dark',
 icon: <MoonIconSolid className="h-4 w-4" />,
 description: 'Dark mode for comfortable viewing',
 preview: 'bg-bgSection border-gray-700'
 },
 {
 id: 'system',
 name: 'System',
 icon: <ComputerDesktopIconSolid className="h-4 w-4" />,
 description: 'Follows your system preference',
 preview: 'bg-gradient-to-r from-gray-100 to-gray-800 border-gray-400'
 }
 ];

 const currentTheme = themes.find(t => t.id === theme) || themes[2];

 return (
 <div className={`relative ${className}`}>
 <button
 onClick={() => setIsOpen(!isOpen)}
 className="flex items-center gap-2 px-3 py-2 bg-bgSection hover:bg-gray-700 border border-gray-600 hover:border-gray-500 rounded-lg text-muted hover:text-white transition-colors"
 title="Change theme"
 >
 {currentTheme.icon}
 <span className="text-sm font-medium hidden sm:block">{currentTheme.name}</span>
 <svg 
 className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
 fill="none" 
 stroke="currentColor" 
 viewBox="0 0 24 24"
 >
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
 </svg>
 </button>

 {isOpen && (
 <>
 {/* Backdrop */}
 <div 
 className="fixed inset-0 z-10" 
 onClick={() => setIsOpen(false)}
 />
 
 {/* Dropdown */}
 <div className="absolute right-0 mt-2 w-64 bg-bgSection border border-gray-700 rounded-lg shadow-lg z-20">
 <div className="p-3 border-b border-gray-700">
 <div className="flex items-center gap-2">
 <SwatchIcon className="h-4 w-4 text-muted" />
 <span className="text-sm font-medium text-white">Choose Theme</span>
 </div>
 </div>
 
 <div className="p-2 space-y-1">
 {themes.map((themeOption) => (
 <button
 key={themeOption.id}
 onClick={() => handleThemeChange(themeOption.id)}
 className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
 theme === themeOption.id
 ? 'bg-blue-500/20 border border-blue-500/30 text-blue-400'
 : 'hover:bg-gray-700/50 text-muted hover:text-white'
 }`}
 >
 <div className={`p-1 rounded ${themeOption.preview}`}>
 {themeOption.icon}
 </div>
 <div className="flex-1 text-left">
 <div className="text-sm font-medium">{themeOption.name}</div>
 <div className="text-xs text-gray-500">{themeOption.description}</div>
 </div>
 {theme === themeOption.id && (
 <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
 )}
 </button>
 ))}
 </div>
 
 <div className="p-3 border-t border-gray-700">
 <div className="flex items-center gap-2 text-xs text-gray-500">
 <SunIcon className="h-3 w-3" />
 <span>Tip: Use system theme to automatically switch between light and dark based on your device settings.</span>
 </div>
 </div>
 </div>
 </>
 )}
 </div>
 );
};

export default ThemeToggle;


